/**
 * BaseChartComponent
 *
 * Base component for all chart controls
 * Provides common chart rendering logic, data binding, error handling
 * Uses SVG-based rendering with Recharts-compatible patterns
 * Supports both manual data (control.config.staticData) and API-based data
 */

import React, { useMemo, memo, useState, useEffect, useRef, useCallback } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';

/** Chart series configuration from control config */
export interface ChartSeriesConfig {
  id: string;
  name: string;
  dataKey: string;
  color?: string;
  type?: string;
  options?: Record<string, unknown>;
}

/** Chart data point type */
export interface ChartDataPoint {
  [key: string]: string | number | undefined;
}

export interface BaseChartComponentProps extends Omit<ControlRendererProps, 'onChange' | 'onBlur'> {
  onChange?: (value: any) => void;
  onBlur?: () => void;
  /** Chart type name for styling */
  chartType: string;
  /** Custom chart implementation */
  children?: React.ReactNode;
  /** Callback when chart is ready */
  onChartReady?: () => void;
}

/**
 * Safely extract and validate data from control value
 */
export function extractChartData(
  value: unknown,
  fallback: ChartDataPoint[] = [],
): ChartDataPoint[] {
  if (!value) return fallback;
  if (!Array.isArray(value)) return fallback;

  return value.filter((item) => typeof item === 'object' && item !== null) as ChartDataPoint[];
}

/**
 * Get series configuration from control config
 */
export function getSeriesConfig(control: ControlRendererProps['control']): ChartSeriesConfig[] {
  const series = control.config?.series as unknown;
  if (!Array.isArray(series)) return [];

  return series
    .filter((s) => typeof s === 'object' && s !== null && 'id' in s)
    .map((s) => ({
      id: (s as Record<string, unknown>).id as string,
      name: ((s as Record<string, unknown>).name as string) || 'Unknown',
      dataKey: ((s as Record<string, unknown>).dataKey as string) || '',
      color: (s as Record<string, unknown>).color as string | undefined,
      type: (s as Record<string, unknown>).type as string | undefined,
      options: (s as Record<string, unknown>).options as Record<string, unknown> | undefined,
    }));
}

/**
 * Get API configuration from control config
 */
function getApiConfig(control: ControlRendererProps['control']) {
  const cfg = control.config as Record<string, unknown> | undefined;
  return {
    dataSourceType: (cfg?.dataSourceType as string | undefined) ?? 'manual',
    apiEndpoint: (cfg?.apiEndpoint as string | undefined) ?? '',
    apiFieldMapping: (cfg?.apiFieldMapping as { xField: string; yField: string } | undefined)
      ?? { xField: 'name', yField: 'value' },
    refreshInterval: (cfg?.refreshInterval as number | undefined) ?? 0,
  };
}

/**
 * BaseChartComponent
 * Handles common chart rendering logic and data validation
 * Performance: Memoized to prevent unnecessary re-renders
 */
export const BaseChartComponent: React.FC<BaseChartComponentProps> = memo(({
  control,
  value,
  chartType,
  children,
  onChartReady,
}) => {
  const containerClassName = getBaseControlClassName(control, {
    disabled: control.disabled,
  });

  // API data source state
  const { dataSourceType, apiEndpoint, apiFieldMapping, refreshInterval } = useMemo(
    () => getApiConfig(control),
    [control.config],
  );

  const [apiData, setApiData] = useState<ChartDataPoint[] | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchApiData = useCallback(async () => {
    if (dataSourceType !== 'api' || !apiEndpoint) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setApiLoading(true);
    setApiError(null);
    try {
      const response = await fetch(apiEndpoint, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json: unknown = await response.json();
      if (!Array.isArray(json)) throw new Error('Response must be a JSON array');
      const transformed = (json as Record<string, unknown>[])
        .filter((item) => typeof item === 'object' && item !== null)
        .map((item) => ({
          ...item,
          name: item[apiFieldMapping.xField],
          value: item[apiFieldMapping.yField],
        })) as ChartDataPoint[];
      setApiData(transformed);
      setApiError(null);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setApiError(err instanceof Error ? err.message : 'Fetch failed');
      setApiData(null);
    } finally {
      setApiLoading(false);
    }
  }, [dataSourceType, apiEndpoint, apiFieldMapping.xField, apiFieldMapping.yField]);

  useEffect(() => {
    void fetchApiData();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchApiData]);

  useEffect(() => {
    if (!refreshInterval || refreshInterval <= 0) return;
    const id = setInterval(() => {
      void fetchApiData();
    }, refreshInterval);
    return () => clearInterval(id);
  }, [fetchApiData, refreshInterval]);

  // Extract and validate chart data
  const chartData = useMemo(() => {
    // API mode: use fetched data
    if (dataSourceType === 'api' && apiData !== null) return apiData;

    // CRITICAL: Manual mode - ONLY use staticData, NO fallback to value prop
    if (dataSourceType === 'manual') {
      const staticData = control.config?.staticData as ChartDataPoint[] | undefined;
      return staticData || [];
    }

    // Fallback for unknown dataSourceType
    return [];
  }, [dataSourceType, apiData, control.config?.staticData]);

  // Get series configuration
  const seriesConfig = useMemo(() => {
    return getSeriesConfig(control);
  }, [control]);

  // Get chart height from config
  const height = useMemo(() => {
    const configHeight = control.config?.height as number | undefined;
    return configHeight || 400;
  }, [control.config?.height]);

  // Get chart title
  const title = useMemo(() => {
    const configTitle = control.config?.title as string | undefined;
    return configTitle || control.label;
  }, [control.config?.title, control.label]);

  // Get legend config
  const legendConfig = useMemo(() => {
    const config = control.config?.legend as Record<string, unknown> | undefined;
    return {
      visible: config?.visible !== false,
      position: (config?.position as string) || 'right',
    };
  }, [control.config?.legend]);

  // Trigger ready callback on mount
  React.useEffect(() => {
    if (onChartReady) {
      onChartReady();
    }
  }, [onChartReady]);

  // API loading state
  if (apiLoading) {
    return (
      <div className={containerClassName}>
        {title && <div className="control__label">{title}</div>}
        <div
          className="control__chart-placeholder"
          style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <p style={{ color: '#9CA3AF' }}>Loading chart data...</p>
        </div>
      </div>
    );
  }

  // API error state
  if (apiError) {
    return (
      <div className={containerClassName}>
        {title && <div className="control__label">{title}</div>}
        <div
          className="control__chart-placeholder"
          style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '4px',
          }}
        >
          <p style={{ color: '#dc2626' }}>Error: {apiError}</p>
        </div>
      </div>
    );
  }

  // Validation and error states
  if (!chartData || chartData.length === 0) {
    return (
      <div className={containerClassName}>
        {title && <div className="control__label">{title}</div>}
        <div
          className="control__chart-placeholder"
          style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <div style={{ color: '#9CA3AF', textAlign: 'center' }}>
            <p>No data available</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Bind chart to a data source
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (seriesConfig.length === 0) {
    return (
      <div className={containerClassName}>
        {title && <div className="control__label">{title}</div>}
        <div
          className="control__chart-placeholder"
          style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <div style={{ color: '#9CA3AF', textAlign: 'center' }}>
            <p>No series configured</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Configure chart series in designer
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {title && <div className="control__label">{title}</div>}

      <div
        className={`control__chart control__chart--${chartType}`}
        style={{ height: `${height}px`, width: '100%' }}
      >
        {children ? (
          React.cloneElement(children as React.ReactElement, {
            data: chartData,
            series: seriesConfig,
            legend: legendConfig,
            height,
          })
        ) : (
          <div style={{ padding: '20px', color: '#666' }}>
            Chart implementation needed
          </div>
        )}
      </div>

      {control.helpText && <div className="control__help">{control.helpText}</div>}
    </div>
  );
});

BaseChartComponent.displayName = 'BaseChartComponent';
